// Site inventory rules: how a Netlify site or Cloudways app (a hosting_inventory row)
// stands against the uptime monitor and hosting accounts. Plain JS so the hosting
// runner's dry run (`runner.mjs inventory`) counts states with the same code the
// Site inventory screen renders. Types: siteInventoryRules.d.mts.

/** Host without scheme, path, port or www, lower-case. */
export const bareHost = (urlOrHost) =>
  String(urlOrHost || '')
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/[/?#].*$/, '')
    .replace(/:\d+$/, '')
    .replace(/^www\./i, '')
    .toLowerCase();

// Second-level suffixes where the registrable domain is three labels (example.co.uk).
const TWO_PART_TLDS = new Set(['co.uk', 'org.uk', 'com.au', 'net.au', 'co.nz', 'com.mx', 'com.br']);

/** The registrable domain: admin.example.com → example.com. */
export function rootDomain(host) {
  const parts = bareHost(host).split('.').filter(Boolean);
  if (parts.length <= 2) return parts.join('.');
  const last2 = parts.slice(-2).join('.');
  return parts.slice(TWO_PART_TLDS.has(last2) ? -3 : -2).join('.');
}

/** Hosts that are an app or admin, not the marketing site: they skip setup's guess and start as 'app'. */
export const APP_HOST = /^(admin|app|reg|portal|submit)\./i;
export const siteKindFor = (host) => (APP_HOST.test(bareHost(host)) ? 'app' : null);

/** Every host the row answers to (primary first). */
export const rowHosts = (row) => [row.primary_domain, ...(row.aliases || [])].filter(Boolean).map(bareHost);

/** The URL a monitored site for this row should watch. Rows with no custom domain use the provider's own host. */
export function urlFor(row) {
  if (row.primary_domain) return `https://${bareHost(row.primary_domain)}/`;
  if (row.provider === 'netlify') return `https://${row.name}.netlify.app/`;
  const fqdn = (row.aliases || [])[0];
  return fqdn ? `https://${bareHost(fqdn)}/` : null;
}

const linkedTo = (row, site) =>
  row.provider === 'netlify' ? site.netlify_site_id === row.external_id : site.cloudways_app_id === row.app_id;

// The monitored site is linked to the other provider (a Netlify site id on a Cloudways row, or the reverse).
const otherProvider = (row, site) => (row.provider === 'netlify' ? Boolean(site.cloudways_app_id) : Boolean(site.netlify_site_id));

/**
 * Monitored sites that are this row: linked by the provider id first, then any whose host is one of the row's.
 * `byId` marks the id links. A host match doesn't count when the domain doesn't answer from this provider and the
 * monitored site is linked to the other one: that's an old app still listing a domain that moved.
 */
export function matchSites(row, sites) {
  const hosts = rowHosts(row);
  const out = [];
  for (const s of sites) {
    const byId = linkedTo(row, s);
    if (byId) out.push({ site: s, byId });
    else if (hosts.includes(bareHost(s.url)) && !(row.serves_here === false && otherProvider(row, s))) out.push({ site: s, byId });
  }
  return out.sort((a, b) => Number(b.byId) - Number(a.byId));
}

/** Monitored sites on this row's domain that another provider serves: this row is the leftover. */
export const movedTo = (row, sites) =>
  row.serves_here === false ? sites.filter((s) => rowHosts(row).includes(bareHost(s.url)) && otherProvider(row, s)) : [];

/**
 * attached: a matching monitored site is on a hosting account. monitored: in the uptime monitor, no account.
 * unmonitored: nothing watches it. ignored: a person said it isn't ours to host (only when not attached).
 * Warnings sit on top of any state.
 */
export function classify(row, sites) {
  const matches = matchSites(row, sites);
  const attached = matches.find((m) => m.site.hosting_account_id);
  const state = attached ? 'attached' : row.decision === 'ignored' ? 'ignored' : matches.length ? 'monitored' : 'unmonitored';
  const warnings = [];
  const label = row.provider === 'netlify' ? 'Netlify' : 'Cloudways';
  if (row.serves_here === false && matches.some((m) => m.byId)) {
    warnings.push({ kind: 'not_serving', text: `Linked, but ${row.primary_domain} no longer answers from ${label}` });
  }
  const moved = movedTo(row, sites);
  if (moved.length) {
    const other = row.provider === 'netlify' ? 'Cloudways' : 'Netlify';
    warnings.push({ kind: 'moved', text: `${bareHost(moved[0].url)} now runs on ${other}; this ${label} ${row.provider === 'netlify' ? 'site' : 'app'} looks left over` });
  }
  // A monitored host pointing at another site of the same provider.
  const elsewhere = matches.filter((m) => !m.byId && (row.provider === 'netlify' ? m.site.netlify_site_id : m.site.cloudways_app_id));
  if (elsewhere.length) warnings.push({ kind: 'linked_elsewhere', text: `${bareHost(elsewhere[0].site.url)} is linked to another ${label} site` });
  if (row.primary_domain) {
    const dupes = sites.filter((s) => bareHost(s.url) === bareHost(row.primary_domain));
    if (dupes.length > 1) warnings.push({ kind: 'duplicate', text: `${dupes.length} uptime-monitor rows for ${bareHost(row.primary_domain)} (www / no-www count as one)` });
  }
  return { state, matches, attachedTo: attached?.site.hosting_account_id ?? null, warnings };
}

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
// Repo names carry the part of the product: petespanels-admin → petespanels.
const repoBase = (repo) => norm(String(repo || '').split('/').pop().replace(/[-_](admin|app|portal|api|site|web|www|astro|frontend|cms)$/i, ''));

// Per-account hosts and repos, worked out once per context object (the screen asks for every row).
const prepared = new WeakMap();
function prepare(ctx) {
  let p = prepared.get(ctx);
  if (p) return p;
  const attached = ctx.sites.filter((s) => s.hosting_account_id);
  const labels = new Map();
  for (const s of attached) {
    const set = labels.get(s.hosting_account_id) || new Set();
    set.add(norm(rootDomain(s.url).split('.')[0]));
    labels.set(s.hosting_account_id, set);
  }
  for (const r of ctx.inventory || []) {
    if (!r.repo) continue;
    const to = classify(r, attached).attachedTo;
    if (to) (labels.get(to) || labels.set(to, new Set()).get(to)).add(repoBase(r.repo));
  }
  p = { attached, labels };
  prepared.set(ctx, p);
  return p;
}

/**
 * Accounts this row probably belongs to, best first, each with why:
 * a sibling host on the account (admin.x.com → x.com's account), the account's client named like the domain,
 * the repo named like the account, one of its hosts or one of its repos, or a name that starts the same.
 */
export function suggestAccounts(row, ctx) {
  const { accounts, clients } = ctx;
  const { attached, labels } = prepare(ctx);
  const out = new Map();
  const add = (accountId, reason, rank) => {
    if (!accountId || !accounts.some((a) => a.id === accountId)) return;
    const prev = out.get(accountId);
    if (!prev || rank < prev.rank) out.set(accountId, { accountId, reason, rank });
  };
  const host = row.primary_domain ? bareHost(row.primary_domain) : null;
  const root = host ? rootDomain(host) : null;
  const label = root ? norm(root.split('.')[0]) : '';
  if (root) for (const s of attached) if (rootDomain(s.url) === root) add(s.hosting_account_id, `Same domain as ${bareHost(s.url)}`, 0);
  if (label.length >= 4) {
    for (const a of accounts) {
      const client = clients.find((c) => c.id === a.client_id);
      if (client && norm(client.name) === label) add(a.id, `Client ${client.name}`, 1);
      else if (norm(a.name) === label) add(a.id, `Named like ${root}`, 1);
    }
  }
  const base = repoBase(row.repo);
  if (base.length >= 4) {
    for (const a of accounts) if (norm(a.name) === base || labels.get(a.id)?.has(base)) add(a.id, `Repo ${row.repo}`, 2);
  }
  // Last: the site's name or domain is the start of an account's name, or the reverse (telluride-tire → Telluride Tire & Auto).
  const names = [norm(row.name), label].filter((n) => n.length >= 6);
  for (const a of accounts) {
    const an = norm(a.name);
    if (an.length >= 6 && names.some((n) => an.startsWith(n) || n.startsWith(an))) add(a.id, `Named like ${row.primary_domain ? root : row.name}`, 3);
  }
  return [...out.values()].sort((x, y) => x.rank - y.rank).map(({ accountId, reason }) => ({ accountId, reason }));
}
